
import { useScrollAnimation, getAnimationClass } from '../../../hooks/useScrollAnimation';

function AnimatedSection({ children, animation = 'slide-up' as 'fade-in' | 'slide-up' | 'slide-left' | 'slide-right' | 'scale-in', delay = 0, className = '' }: { children: React.ReactNode; animation?: 'fade-in' | 'slide-up' | 'slide-left' | 'slide-right' | 'scale-in'; delay?: number; className?: string }) {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <div ref={ref} className={`${getAnimationClass(isVisible, animation, delay)} ${className}`}>
      {children}
    </div>
  );
}

export default function Team() {
  const team = [
    {
      name: 'Dr. Alejandro Vega',
      role: 'CEO & Co-Fundador',
      bio: 'Oftalmólogo con 15 años de experiencia clínica. Lideró la visión de Anamnesy Soft desde la perspectiva médica.',
      image: 'https://readdy.ai/api/search-image?query=professional%20latino%20male%20CEO%20in%20modern%20dark%20suit%20smiling%20confidently%20corporate%20headshot%20portrait%20clean%20bright%20background%20warm%20lighting%20executive%20business%20photography&width=400&height=450&seq=team-ceo-1&orientation=portrait',
    },
    {
      name: 'Ing. Sofía Ramírez',
      role: 'CTO & Co-Fundadora',
      bio: 'Ingeniera de software con experiencia en startups de salud digital. Arquitecta principal de la plataforma Anamnesy Soft.',
      image: 'https://readdy.ai/api/search-image?query=professional%20latina%20female%20CTO%20technology%20executive%20in%20elegant%20business%20attire%20smiling%20warmly%20corporate%20headshot%20portrait%20clean%20bright%20background%20modern%20professional%20photography&width=400&height=450&seq=team-cto-1&orientation=portrait',
    },
    {
      name: 'Dr. Martín Herrera',
      role: 'Director Médico',
      bio: 'Especialista en retina y glaucoma. Supervisa que cada funcionalidad cumpla con los estándares clínicos más exigentes.',
      image: 'https://readdy.ai/api/search-image?query=professional%20male%20doctor%20ophthalmologist%20in%20white%20coat%20with%20glasses%20smiling%20corporate%20headshot%20portrait%20clean%20bright%20background%20warm%20lighting%20medical%20professional%20photography&width=400&height=450&seq=team-medical-1&orientation=portrait',
    },
    {
      name: 'Lic. Valentina Cruz',
      role: 'VP de Producto',
      bio: 'Experta en UX para salud digital. Lidera el diseño de experiencias intuitivas que los profesionales aman usar.',
      image: 'https://readdy.ai/api/search-image?query=professional%20young%20latina%20female%20product%20designer%20in%20smart%20casual%20attire%20smiling%20confidently%20corporate%20headshot%20portrait%20clean%20bright%20background%20modern%20creative%20professional%20photography&width=400&height=450&seq=team-product-1&orientation=portrait',
    },
    {
      name: 'Ing. Ricardo Morales',
      role: 'VP de Ingeniería',
      bio: 'Arquitecto de sistemas cloud con experiencia en plataformas de alta disponibilidad para el sector salud.',
      image: 'https://readdy.ai/api/search-image?query=professional%20latino%20male%20software%20engineer%20in%20casual%20business%20attire%20smiling%20corporate%20headshot%20portrait%20clean%20bright%20background%20warm%20lighting%20tech%20professional%20photography&width=400&height=450&seq=team-eng-1&orientation=portrait',
    },
    {
      name: 'Dra. Camila Ortiz',
      role: 'Directora de Éxito del Cliente',
      bio: 'Optometrista de formación, lidera el equipo de soporte y asegura que cada cliente alcance sus objetivos con la plataforma.',
      image: 'https://readdy.ai/api/search-image?query=professional%20latina%20female%20customer%20success%20director%20in%20elegant%20business%20attire%20smiling%20warmly%20corporate%20headshot%20portrait%20clean%20bright%20background%20modern%20professional%20photography&width=400&height=450&seq=team-cs-1&orientation=portrait',
    },
  ];

  return (
    <section className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <AnimatedSection animation="fade-in">
          <div className="text-center mb-16">
            <span className="inline-block text-xs font-semibold text-emerald-600 uppercase tracking-widest mb-3">Nuestro Equipo</span>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Las personas detrás de <span className="font-display italic text-emerald-600">Anamnesy Soft</span>
            </h2>
            <p className="text-sm text-gray-500 max-w-lg mx-auto">
              Un equipo multidisciplinario que combina experiencia clínica oftalmológica con innovación tecnológica de vanguardia.
            </p>
          </div>
        </AnimatedSection>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {team.map((member, index) => (
            <AnimatedSection key={index} animation="scale-in" delay={index * 100}>
              <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-lg transition-all cursor-pointer group h-full">
                <div className="w-full h-56 overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-base font-bold text-gray-900">{member.name}</h3>
                  <p className="text-xs font-semibold text-emerald-600 mb-3">{member.role}</p>
                  <p className="text-sm text-gray-500 leading-relaxed">{member.bio}</p>
                  <div className="flex items-center space-x-2 mt-4 pt-4 border-t border-gray-100">
                    <a href="#" rel="nofollow" className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-emerald-500 hover:text-white transition-colors cursor-pointer text-gray-500">
                      <i className="ri-linkedin-fill text-sm"></i>
                    </a>
                    <a href="#" rel="nofollow" className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-emerald-500 hover:text-white transition-colors cursor-pointer text-gray-500">
                      <i className="ri-twitter-x-fill text-sm"></i>
                    </a>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
}
